import asyncSeries from 'async/series';
import React from 'react';
import {
  Routes,
  Route,
  useOutlet,
  useLocation,
  useNavigate,
  Navigate,
} from 'react-router-dom';

import ModalDialog from './components/dialogs/modal_dialog';
import StandardScreen from './components/standard_screen';

import SignupScreen from './signup_screen';

import AccountScreen from './account_screen';
import BucketEditScreen from './bucket_edit_screen';
import BulkDeleteScreen from './bulk_delete_screen';
import BulkUndeleteScreen from './bulk_undelete_screen';
import CreateFolderScreen from './create_folder_screen';
import DeviceScreen from './device_screen';
import DeviceListScreen from './device_list_screen';
import DownloadScreen from './download_screen';
import HomeScreen from './home_screen';
import LoadingScreen from './loading_screen';
import LoginScreen from './login_screen';
import LogoutScreen from './logout_screen';
import MediaGrantScreen from './media_grant_screen';
import NotFoundScreen from './not_found_screen';
import OrgAppListScreen from './org_app_list_screen';
import OrgAppScreen from './org_app_screen';
import OrgInvoiceListScreen from './org_invoice_list_screen';
import OrgInvoiceScreen from './org_invoice_screen';
import OrgPaymentAddScreen from './org_payment_add_screen';
import OrgPaymentScreen from './org_payment_screen';
import OrgPlanScreen from './org_plan_screen';
import OrgS3Screen from './org_s3_screen';
import OrgScreen from './org_screen';
import UploadScreen from './upload_screen';
import UserEditScreen from './user_edit_screen';
import UserListScreen from './user_list_screen';
import VolumeAuditScreen from './volume_audit_screen';
import VolumeGrantScreen from './volume_grant_screen';
import VolumeListGrantScreen from './volume_list_grant_screen';
import VolumeListScreen from './volume_list_screen';
import VolumeMetricScreen from './volume_metric_screen';
import VolumePermissionScreen from './volume_permission_screen';
import VolumeSettingScreen from './volume_setting_screen';

import Stores from './stores';
import Managers from './managers';

import UserStore from './stores/user_store';

import Analytics from './tools/analytics';
import Api from './tools/api';
import util from './tools/util';

import './css/colors.css';

const _startup = util.herdOnce((done: any) => {
  util.log('ares-web: startup:', util.getVersion());
  util.setDebugLogger(Analytics.log);
  Analytics.init();
  asyncSeries([Api.init, Stores.init, Managers.init], (err) => {
    if (err) {
      util.errorLog('ares-web: startup err:', err);
    }
    util.log('ares-web: startup done');
    Analytics.loadFinish();
    done();
  });
});

export default function App() {
  const [is_loading, setLoading] = React.useState(true);
  const start_url = useLocation()?.pathname;
  const navigate = useNavigate();
  React.useEffect(
    () =>
      _startup(() => {
        if (!UserStore.isReady()) {
          navigate('/loading', { state: { from: start_url ?? '/' } });
        }
        setLoading(false);
      }),
    //eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
  let content: any = null;
  if (is_loading) {
    content = <LoadingScreen noNav={true} />;
  } else {
    content = (
      <>
        <Routes>
          <Route path='/loading' element={<LoadingScreen forever={true} />} />
          <Route path='/login' element={<LoginScreen />} />
          <Route path='/logout' element={<LogoutScreen />} />
          <Route path='/signup' element={<SignupScreen />} />
          <Route element={<ProtectedLayout />}>
            <Route path='/' element={<HomeScreen />} />
            <Route path='/home' element={<HomeScreen />} />
            <Route path='/home/:grant_name/*' element={<MediaGrantScreen />} />
            <Route
              path='/download/:grant_name/*'
              element={<DownloadScreen />}
            />
            <Route path='/upload/:grant_name/*' element={<UploadScreen />} />
            <Route
              path='/create_folder/:grant_name/*'
              element={<CreateFolderScreen />}
            />
            <Route
              path='/bulk/delete/:grant_name/*'
              element={<BulkDeleteScreen />}
            />
            <Route
              path='/bulk/undelete/:grant_name/*'
              element={<BulkUndeleteScreen />}
            />
            <Route path='/volume' element={<VolumeListScreen />} />
            <Route
              path='/volume/permission'
              element={<VolumeListGrantScreen />}
            />
            <Route
              path='/volume/:volume_id'
              element={<VolumeSettingScreen />}
            />
            <Route
              path='/volume/:volume_id/audit'
              element={<VolumeAuditScreen />}
            />
            <Route
              path='/volume/:volume_id/grant/:grant_name'
              element={<VolumeGrantScreen />}
            />
            <Route
              path='/volume/:volume_id/metric'
              element={<VolumeMetricScreen />}
            />
            <Route
              path='/volume/:volume_id/permission'
              element={<VolumePermissionScreen />}
            />
            <Route path='/device' element={<DeviceListScreen />} />
            <Route path='/device/:device_id' element={<DeviceScreen />} />
            <Route path='/account' element={<AccountScreen />} />
            <Route path='/org' element={<OrgScreen />} />
            <Route path='/org/app' element={<OrgAppListScreen />} />
            <Route path='/org/app/:app_id' element={<OrgAppScreen />} />
            <Route path='/org/bucket' element={<OrgS3Screen />} />
            <Route
              path='/org/bucket/:s3_bucket_id'
              element={<BucketEditScreen />}
            />
            <Route path='/org/invoice' element={<OrgInvoiceListScreen />} />
            <Route
              path='/org/invoice/:invoice_id'
              element={<OrgInvoiceScreen />}
            />
            <Route path='/org/payment' element={<OrgPaymentScreen />} />
            <Route path='/org/payment/add' element={<OrgPaymentAddScreen />} />
            <Route path='/org/plan' element={<OrgPlanScreen />} />
            <Route path='/admin/user' element={<UserListScreen />} />
            <Route path='/admin/user/:user_id' element={<UserEditScreen />} />
          </Route>
          <Route path='*' element={<NotFoundScreen />} />
        </Routes>
        <ModalDialog />
      </>
    );
  }
  return content;
}
function ProtectedLayout(props) {
  const outlet = useOutlet();
  return <ProtectedRoute>{outlet}</ProtectedRoute>;
}
function ProtectedRoute(props) {
  const is_logged_in = UserStore.useIsLoggedIn();
  const location = useLocation();
  return is_logged_in ? (
    <StandardScreen>{props.children}</StandardScreen>
  ) : (
    <Navigate to='/login' state={{ from: location.pathname }} />
  );
}
